// PL/pgSQL routine PARAMETER extraction (CB4).
//
// PL/pgSQL function / procedure parameters are declared in the routine
// SIGNATURE (`CREATE [OR REPLACE] FUNCTION foo(p_a int, p_b text DEFAULT 'x')`),
// which sits OUTSIDE the dollar-quoted body. The DECLARE-block extractor only
// scans inside `$$ … $$`, so it never surfaces parameters. This helper scans
// the text PRECEDING a body region for the nearest enclosing routine header
// and returns its named parameters so the resolver can surface them inside the
// body at tier 1, alongside DECLARE locals.
//
// Best-effort, source-text driven (no ANTLR): the PG grammar parks the body as
// a single token, so a focused scan is both simpler and more robust to the
// partial / mid-edit buffers the IntelliSense path sees.

export interface PgRoutineParam {
  /** Bare parameter name (PG folds unquoted identifiers to lowercase; quoted
   *  names preserve their spelling). */
  name: string;
  /** Argument mode keyword, lowercased: `in` (default), `out`, `inout`,
   *  `variadic`. */
  mode: string;
  /** Verbatim type text following the name (trimmed); may be empty. */
  typeText: string;
}

/** Extract the named parameters of the routine whose signature immediately
 *  precedes `bodyStart` (the offset just inside the opening dollar-quote).
 *
 *  Returns `[]` for anonymous `DO` blocks (no `(…)` signature) or when no
 *  `FUNCTION` / `PROCEDURE` header is found before the body. */
export function extractPgRoutineParams(
  source: string,
  bodyStart: number,
): PgRoutineParam[] {
  const head = source.slice(0, bodyStart);
  // Find the LAST FUNCTION / PROCEDURE keyword before the body — that's the
  // routine whose body the cursor is in (PG routine definitions don't nest).
  const kwMatch = lastRoutineKeyword(head);
  if (kwMatch === -1) return [];

  // The parameter list is the first balanced `( … )` after the routine
  // keyword + name. Find the opening paren.
  const openParen = source.indexOf('(', kwMatch);
  if (openParen === -1 || openParen >= bodyStart) return [];
  const closeParen = matchBalancedParen(source, openParen);
  if (closeParen === -1 || closeParen >= bodyStart) return [];

  const inner = source.slice(openParen + 1, closeParen);
  return parseParamList(inner);
}

/** Index of the last whole-word `FUNCTION` or `PROCEDURE` keyword in `text`,
 *  or -1. Word-boundary anchored so an identifier substring can't false-match. */
function lastRoutineKeyword(text: string): number {
  const re = /\b(FUNCTION|PROCEDURE)\b/gi;
  let last = -1;
  for (const m of text.matchAll(re)) last = m.index;
  return last;
}

/** Given the offset of an opening `(`, return the offset of the matching
 *  `)`, accounting for nested parens, single-quoted strings, dollar-quotes,
 *  and comments. Returns -1 when unbalanced. */
function matchBalancedParen(source: string, open: number): number {
  let depth = 0;
  let i = open;
  while (i < source.length) {
    const ch = source[i];
    if (ch === '(') {
      depth += 1;
      i += 1;
      continue;
    }
    if (ch === ')') {
      depth -= 1;
      if (depth === 0) return i;
      i += 1;
      continue;
    }
    if (ch === "'") {
      i = skipSingleQuote(source, i);
      continue;
    }
    if (ch === '-' && source[i + 1] === '-') {
      const nl = source.indexOf('\n', i);
      i = nl === -1 ? source.length : nl + 1;
      continue;
    }
    if (ch === '/' && source[i + 1] === '*') {
      const close = source.indexOf('*/', i + 2);
      i = close === -1 ? source.length : close + 2;
      continue;
    }
    i += 1;
  }
  return -1;
}

function skipSingleQuote(source: string, start: number): number {
  let i = start + 1;
  while (i < source.length) {
    if (source[i] === "'") {
      if (source[i + 1] === "'") {
        i += 2;
        continue;
      }
      return i + 1;
    }
    i += 1;
  }
  return source.length;
}

const ARG_MODES = new Set(['in', 'out', 'inout', 'variadic']);

/** Split a parameter list on top-level commas and parse each entry's name +
 *  mode + type. */
function parseParamList(inner: string): PgRoutineParam[] {
  const out: PgRoutineParam[] = [];
  for (const raw of splitTopLevelCommas(inner)) {
    const param = parseOneParam(raw.trim());
    if (param) out.push(param);
  }
  return out;
}

/** Parse one parameter entry: `[mode] [name] type [{DEFAULT|=} expr]`.
 *
 *  PG allows the name to be omitted (positional-only). We only surface
 *  parameters that HAVE a name — an unnamed parameter has nothing to complete.
 *  The grammar makes name-vs-type ambiguous without a type catalog, so we use
 *  the documented heuristic: after stripping a leading mode keyword, the FIRST
 *  token is the name when there is at least one more token (the type); a lone
 *  token is a type-only positional parameter (no name) and is skipped. */
function parseOneParam(entry: string): PgRoutineParam | null {
  if (entry.length === 0) return null;
  // Strip any DEFAULT / = initializer tail (top-level).
  const initIdx = findInitializer(entry);
  const head = (initIdx === -1 ? entry : entry.slice(0, initIdx)).trim();
  if (head.length === 0) return null;

  const tokens = tokenizeParamHead(head);
  if (tokens.length === 0) return null;

  // PG argmode is a single leading keyword: IN (default) / OUT / INOUT /
  // VARIADIC. (Unlike Oracle / T-SQL there is no two-word `IN OUT` form.)
  let mode = 'in';
  let idx = 0;
  const first = tokens[0].toLowerCase();
  if (ARG_MODES.has(first)) {
    mode = first;
    idx = 1;
  }

  const remaining = tokens.slice(idx);
  if (remaining.length < 2) {
    // Only a single token after the (optional) mode → type-only positional
    // parameter with no name. Nothing to complete.
    return null;
  }
  const name = unquoteIdent(remaining[0]);
  const typeText = remaining.slice(1).join(' ');
  if (name.length === 0) return null;
  return { name, mode, typeText };
}

/** Locate the start of a top-level `DEFAULT` keyword or bare `=` initializer
 *  in a parameter entry, or -1. */
function findInitializer(entry: string): number {
  const def = entry.search(/\bDEFAULT\b/i);
  const eq = entry.indexOf('=');
  if (def === -1) return eq;
  if (eq === -1) return def;
  return Math.min(def, eq);
}

/** Split parameter-head text into whitespace-separated tokens, keeping
 *  bracketed type suffixes (`numeric(10,2)`, `int[]`, `"My Name"`) attached to
 *  the preceding token. */
function tokenizeParamHead(head: string): string[] {
  const tokens: string[] = [];
  let i = 0;
  const len = head.length;
  while (i < len) {
    const ch = head[i];
    if (/\s/.test(ch)) {
      i += 1;
      continue;
    }
    if (ch === '"') {
      // Quoted identifier — read to closing quote (handles `""` escape).
      let j = i + 1;
      let acc = '"';
      while (j < len) {
        acc += head[j];
        if (head[j] === '"') {
          if (head[j + 1] === '"') {
            acc += '"';
            j += 2;
            continue;
          }
          j += 1;
          break;
        }
        j += 1;
      }
      tokens.push(acc);
      i = j;
      continue;
    }
    // Read a bare token, absorbing any following parenthesis / bracket group
    // so `numeric(10, 2)` and `int[]` stay attached.
    let j = i;
    let acc = '';
    while (j < len && !/\s/.test(head[j]) && head[j] !== '(' && head[j] !== '[') {
      acc += head[j];
      j += 1;
    }
    while (j < len && (head[j] === '(' || head[j] === '[')) {
      const open = head[j];
      const close = open === '(' ? ')' : ']';
      let depth = 0;
      while (j < len) {
        if (head[j] === open) depth += 1;
        else if (head[j] === close) {
          depth -= 1;
          acc += head[j];
          j += 1;
          if (depth === 0) break;
          continue;
        }
        acc += head[j];
        j += 1;
      }
    }
    if (acc.length > 0) tokens.push(acc);
    i = j;
  }
  return tokens;
}

/** Split on commas that are not inside parens / brackets / quotes. */
function splitTopLevelCommas(text: string): string[] {
  const out: string[] = [];
  let depth = 0;
  let start = 0;
  let i = 0;
  while (i < text.length) {
    const ch = text[i];
    if (ch === '(' || ch === '[') depth += 1;
    else if (ch === ')' || ch === ']') depth -= 1;
    else if (ch === "'") {
      i = skipSingleQuote(text, i);
      continue;
    } else if (ch === '"') {
      let j = i + 1;
      while (j < text.length && text[j] !== '"') j += 1;
      i = j + 1;
      continue;
    } else if (ch === ',' && depth === 0) {
      out.push(text.slice(start, i));
      start = i + 1;
    }
    i += 1;
  }
  out.push(text.slice(start));
  return out;
}

function unquoteIdent(text: string): string {
  const t = text.trim();
  if (t.startsWith('"') && t.endsWith('"') && t.length >= 2) {
    return t.slice(1, -1).replace(/""/g, '"');
  }
  return t.toLowerCase();
}
